/**
 * Kupon Conversion Template — Main Orchestrator
 *
 * Renders all sections from the rosella-flow-convert design,
 * powered by dynamic data from the LP builder database.
 *
 * Checkout flow wired to project's addToCart → /checkout/pengiriman
 */

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useApp } from "../../../context/AppContext";
import { formatRupiah } from "../../../lib/format";
import { CONTACT_CONFIG } from "../../../lib/site";
import { initMetaPixel, trackEvent, getGlobalPixelId } from "../../../lib/metaPixel";
import SEOHead from "../../SEOHead";

import Hero from "./Hero";
import ProblemSection from "./ProblemSection";
import SolutionSection from "./SolutionSection";
import BenefitSection from "./BenefitSection";
import TestimonialSection from "./TestimonialSection";
import HowToUseSection from "./HowToUseSection";
import OfferSection from "./OfferSection";
import HardSellSection from "./HardSellSection";
import UrgencySection from "./UrgencySection";
import GuaranteeSection from "./GuaranteeSection";
import FinalCTA from "./FinalCTA";
import StickyBar from "./StickyBar";

import "./kupon-conversion.css";

// Map section_type → component
const SECTION_MAP = {
  hero: Hero,
  problem: ProblemSection,
  solution: SolutionSection,
  benefit: BenefitSection,
  testimonial: TestimonialSection,
  how_to_use: HowToUseSection,
  offer: OfferSection,
  hard_sell: HardSellSection,
  urgency: UrgencySection,
  guarantee: GuaranteeSection,
  final_cta: FinalCTA,
};

export default function KuponConversionTemplate({ landingPage, product }) {
  const navigate = useNavigate();
  const { addToCart } = useApp();
  const [showSticky, setShowSticky] = useState(false);

  // ─── Derived data ──────────────────────────────────────────────────────────
  const displayName = product?.name || landingPage?.title || "Produk";
  const productImage = landingPage?.custom_image || product?.image || "";
  const price = Number(product?.price || 0);
  const comparePrice = Number(product?.compare_price || 0);
  const hasComparePrice = comparePrice > price && price > 0;
  const stock = Number(product?.stock || 0);

  const ctaConfig = landingPage?.cta_config || {};
  const ctaMode = ctaConfig.mode || "both";
  const checkoutEnabled = ctaMode !== "whatsapp" && ctaConfig.checkout_enabled !== false;
  const whatsappEnabled = ctaMode !== "web" && ctaConfig.whatsapp_enabled !== false;
  const productAvailable = Boolean(product) && product.is_active !== false && stock > 0;
  const canCheckout = checkoutEnabled && productAvailable;

  const whatsappText = ctaConfig.whatsapp_text || `Halo, saya ingin bertanya tentang ${displayName}.`;
  const whatsappUrl = `${CONTACT_CONFIG.whatsappUrl}?text=${encodeURIComponent(whatsappText)}`;

  const pagePath = `/lp/${landingPage?.slug || ""}`;

  // ─── Sections from database ────────────────────────────────────────────────
  const sections = useMemo(() => {
    if (!landingPage) return [];
    return (landingPage.sections || [])
      .filter((s) => s.is_enabled !== false)
      .sort((a, b) => (a.section_order || 0) - (b.section_order || 0))
      .map((s) => ({
        id: s.id,
        type: s.section_type,
        content: s.config_json?.content || s.config_json || {},
        image_source: s.image_source || "product",
        image_url: s.image_url || "",
      }));
  }, [landingPage]);

  // ─── Meta Pixel ────────────────────────────────────────────────────────────
  useEffect(() => {
    const pixelId = ctaConfig.meta_pixel_id || getGlobalPixelId();
    if (pixelId) {
      initMetaPixel(pixelId);
      trackEvent("ViewContent", {
        content_name: displayName,
        content_type: "product",
        content_ids: product?.id ? [String(product.id)] : [],
        value: price,
        currency: "IDR",
      });
    }
  }, [landingPage]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Sticky bar ────────────────────────────────────────────────────────────
  useEffect(() => {
    const handleScroll = () => setShowSticky(window.scrollY > 500);
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // ─── Actions ───────────────────────────────────────────────────────────────
  const handleCheckout = () => {
    if (!canCheckout) {
      toast.error("Produk belum tersedia untuk checkout");
      return;
    }
    addToCart(product.id, 1);
    trackEvent("AddToCart", {
      content_name: displayName,
      content_ids: [String(product.id)],
      content_type: "product",
      value: price,
      currency: "IDR",
    });
    toast.success(`${displayName} ditambahkan ke checkout`);
    setTimeout(() => navigate("/checkout/pengiriman"), 300);
  };

  const handleWhatsapp = () => {
    trackEvent("Lead", { content_name: displayName, content_category: "WhatsApp" });
    window.open(whatsappUrl, "_blank");
  };

  // ─── Get image for a section ───────────────────────────────────────────────
  const getSectionImage = (section) => {
    if (section.image_source === "url" && section.image_url) return section.image_url;
    return productImage;
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <main className="lp-kupon" data-testid="kupon-conversion-lp">
      <SEOHead
        title={landingPage?.seo_title || `${displayName} - Promo`}
        description={landingPage?.seo_description || landingPage?.description}
        path={pagePath}
        image={productImage}
      />

      {sections.map((section) => {
        const Component = SECTION_MAP[section.type];
        if (!Component) return null;

        // Common props for all sections
        const commonProps = {
          key: section.id || section.type,
          content: section.content,
          productImage: getSectionImage(section),
          onCheckout: handleCheckout,
          canCheckout,
          onWhatsapp: handleWhatsapp,
          whatsappEnabled,
        };

        // Extra props for specific sections
        if (section.type === "offer" || section.type === "final_cta") {
          return (
            <Component
              {...commonProps}
              productName={displayName}
              productDesc={product?.short_description || ""}
              price={price}
              comparePrice={comparePrice}
              hasComparePrice={hasComparePrice}
              formatRupiah={formatRupiah}
            />
          );
        }

        return <Component {...commonProps} />;
      })}

      {/* Footer */}
      <footer className="bg-[var(--kp-primary-deep)] text-white/80 py-10 text-center text-sm">
        <div className="font-display text-xl font-bold text-white">{displayName}</div>
        <p className="mt-2">© {new Date().getFullYear()} {displayName}. Semua hak dilindungi.</p>
      </footer>

      {/* Sticky mobile bar */}
      <StickyBar
        price={price}
        formatRupiah={formatRupiah}
        onCheckout={handleCheckout}
        canCheckout={canCheckout}
        visible={showSticky}
      />
    </main>
  );
}
